import { Anchor, Breadcrumbs, Button, Card, Grid, Group, Text } from '@mantine/core';
import { IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../../components/ErrorMessage';
import LockableButton from '../../../components/Lockable/LockableButton';
import Metadata from '../../../components/Metadata';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import ReportsForm from '../../../components/Reports/ReportsForm';
import Spinner from '../../../components/Spinner';
import { Page } from '../../../constants/pageTitles';
import useFetch from '../../../helpers/useFetch';

const page = `${Page['reports']}`;

const ReportsNewPage = () => {
  const { accountId, domainId, reportId } = useParams();
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const [report, setReport] = useState();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [error, setError] = useState();
  const [userError, setUserError] = useState();

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/exports/${reportId}`)
      .then(({ report }) => {
        if (!report) {
          navigate(`/accounts/${accountId}/domains/${domainId}/reports`);
        }
        setReport(report);
      })

      .catch(setError);
  }, [fetch, navigate, accountId, domainId, reportId]);

  const validateReport = (report) => {
    if (!report.name) {
      return false;
    }

    if (report.type === 'oneTime') {
      if (!report.periodStart || !report.periodEnd) {
        return false;
      }
    }

    if (report.type === 'periodic') {
      if (!report.schedule) {
        return false;
      }
    }

    if (!report.scope) {
      return false;
    }

    if (report.selection === 'specific') {
      if (!report.elements.length) {
        return false;
      }
    }

    if (!report.recipients.length) {
      return false;
    }

    return true;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setReport((prevState) => {
      const newState = { ...prevState };

      if (name === 'type' && value === 'oneTime') {
        newState.schedule = null;
      }

      if (name === 'type' && value === 'periodic') {
        newState.periodStart = null;
        newState.periodEnd = null;
      }

      if (name === 'scope') {
        newState.elements = [];
      }

      return { ...newState, [name]: value };
    });
  };

  const updateReport = (report) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/exports/${reportId}`, {
      method: 'PUT',
      body: JSON.stringify(report),
    })
      .then(() => {
        navigate(location.pathname.replace(`/reports/${report.id}/details`, '/reports'));
      })
      .catch(setUserError);
  };

  const deleteReport = (cb) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/exports/${report.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        if (typeof cb === 'function') {
          cb();
        }
        navigate(location.pathname.replace(`/reports/${report.id}/details`, '/reports'));
      })
      .catch(setError);
  };

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!report) {
    return <Spinner />;
  }

  const backButton = (
    <Button variant="default" onClick={() => navigate(-1)}>
      {' '}
      Back
    </Button>
  );

  const deleteButton = (
    <LockableButton restrictionKey="reportsDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
      <IconTrash size={14} /> Delete
    </LockableButton>
  );

  const saveButton = (
    <LockableButton
      restrictionKey="reportsUpdate"
      variant="primary"
      onClick={() => updateReport(report)}
      disabled={!validateReport(report)}
    >
      <IconDeviceFloppy size={14} /> Save
    </LockableButton>
  );

  return (
    <>
      <Metadata title={`${page} | ${report?.name || 'Unknown report'} | Edit`} />
      <Breadcrumbs mb="md">
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/analytics`}>Home</Anchor>
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/reports`}>{page}</Anchor>
        <Anchor>{report?.name || 'Unknown report'}</Anchor>
        <Text>Edit</Text>
      </Breadcrumbs>

      <ConfirmationModal
        show={confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={(cb) => deleteReport(cb)}
        message="This action cannot be undone!"
      />

      <Grid>
        <Grid.Col>
          <Card withBorder>
            <Card.Section inheritPadding py="sm">
              <Grid.Col span={12}>
                <ReportsForm report={report} handleChange={(event) => handleChange(event)} />
                <Group justify="flex-end" gap="xs" py="md">
                  {backButton}
                  {deleteButton}
                  {saveButton}
                </Group>
              </Grid.Col>
            </Card.Section>
          </Card>
        </Grid.Col>
      </Grid>
      {userError ? (
        <div style={{ textAlign: 'right' }}>
          <ErrorMessage message={userError.message} />
        </div>
      ) : null}
    </>
  );
};

export default ReportsNewPage;
